import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { formatNumber, interpolate } from './shared/chartFormat';

// groupdate's default week: 0 = Sunday. Display order is Mon..Sun.
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const LEVELS = 6;

const pad = hour => String(hour).padStart(2, '0');

// PE-438: hour-by-weekday load matrix from /busy_chart. The endpoint is
// pinned to created_at, so this reads as booking-hour load — the subtitle
// copy says so.
export default class AppointmentBusyHoursMatrix extends React.Component {
  constructor(props) {
    super(props);
    this.state = { cells: {}, max: 0, loading: true, error: false };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true, error: false });

    fetch('/api/appointments/busy_chart' + (this.props.query || ''))
      .then(res => (res.ok ? res.json() : Promise.reject(res)))
      .then(json => {
        if (this.unmounted || requestId !== this.requestId) return;
        const cells = {};
        let max = 0;
        ((json && json.data) || []).forEach(cell => {
          cells[`${cell.dow}-${cell.hour}`] = cell.value;
          max = Math.max(max, cell.value);
        });
        this.setState({ cells, max, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ cells: {}, max: 0, loading: false, error: true });
      });
  }

  level(value) {
    const { max } = this.state;
    if (!value || max === 0) return 0;
    return Math.max(1, Math.ceil((value / max) * LEVELS));
  }

  // Busiest single cell and quietest weekday, read straight off the matrix.
  note() {
    const { labels } = this.props;
    const { cells } = this.state;
    const weekdays = labels.weekdays_long.split(',');

    let peak = null;
    const dayTotals = {};
    DOW_ORDER.forEach(dow => {
      dayTotals[dow] = 0;
      HOURS.forEach(hour => {
        const value = cells[`${dow}-${hour}`] || 0;
        dayTotals[dow] += value;
        if (!peak || value > peak.value) peak = { dow, hour, value };
      });
    });
    const quietest = DOW_ORDER.reduce((min, dow) => (dayTotals[dow] < dayTotals[min] ? dow : min), DOW_ORDER[0]);

    return interpolate(labels.busy_note, {
      day: weekdays[peak.dow],
      range: `${pad(peak.hour)}:00–${pad(peak.hour + 1)}:00`,
      quiet_day: weekdays[quietest]
    });
  }

  renderScale() {
    const { labels } = this.props;

    return (
      <div className="appt-stats__scale">
        <span>{labels.busy_scale_low}</span>
        {Array.from({ length: LEVELS }, (unused, index) => (
          <i className={`appt-stats__scale-swatch appt-stats__cell--l${index + 1}`} key={index} />
        ))}
        <span>{labels.busy_scale_high}</span>
      </div>
    );
  }

  renderMatrix() {
    const { labels, locale } = this.props;
    const { cells } = this.state;
    const weekdaysShort = labels.weekdays_short.split(',');
    const weekdaysLong = labels.weekdays_long.split(',');

    return (
      <div className="appt-stats__matrix-scroll">
        <div className="appt-stats__matrix">
          <span className="appt-stats__matrix-corner" />
          {HOURS.map(hour => (
            <span className="appt-stats__matrix-hour" key={hour}>{pad(hour)}</span>
          ))}
          {DOW_ORDER.map(dow => (
            <React.Fragment key={dow}>
              <span className="appt-stats__matrix-day">{weekdaysShort[dow]}</span>
              {HOURS.map(hour => {
                const value = cells[`${dow}-${hour}`] || 0;
                const label = `${weekdaysLong[dow]} ${pad(hour)}:00 · ${formatNumber(value, locale)}`;
                return (
                  <span
                    className={`appt-stats__cell appt-stats__cell--l${this.level(value)}`}
                    key={hour}
                    title={label}
                    role="img"
                    aria-label={label}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { labels } = this.props;
    const { loading, error, max } = this.state;

    return (
      <div>
        <div className="appt-stats__card-head">
          <div>
            <h4 className="card-title mb-1">{labels.busy_title}</h4>
            <p className="appt-stats__card-subtitle">{labels.busy_subtitle}</p>
          </div>
          {!loading && !error && this.renderScale()}
        </div>

        {loading ? (
          <div style={{ padding: '12px 0' }}>
            <Skeleton height={220} borderRadius={14} />
          </div>
        ) : error ? (
          <div className="text-center text-muted py-5">{labels.load_error}</div>
        ) : max === 0 ? (
          <div className="text-center text-muted py-5">{labels.no_data}</div>
        ) : (
          <div>
            {this.renderMatrix()}
            <div className="appt-stats__note">
              <i className="zmdi zmdi-lamp" />
              <span>{this.note()}</span>
            </div>
          </div>
        )}
      </div>
    );
  }
}
